import { UploadOutlined, UserOutlined } from '@ant-design/icons'
import { Dropzone } from '@community_dev/pixels'
import { CommunicationChannel } from '@community_dev/types/lib/api/CommunicationChannel'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert, Avatar, Button, Skeleton, Typography, notification } from 'antd'
import { useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import styled from 'styled-components'

import { hasConfiguredCustomDomain } from '../CustomDomain/utils'
import { useClientSettings } from '../hooks/useClientSettings'
import { useImpersonate } from '../hooks/useImpersonate'
import { SettingsCard, SettingsPage, SettingsRow, SettingsRows } from '../Settings'

import { COMMUNICATION_CHANNEL_LABEL, SMS_PROVIDER_LABELS } from './constants'
import { getMemberCountChannels, useMemberCount } from './queries'

import {
  Client,
  deleteClient,
  getDownloadUrl,
  patchClientSettings,
  postSendVcard,
  putClient,
  putProfileImage,
} from 'api/clients'
import { ButtonModalPrompt } from 'components/ButtonModalPrompt'
import { CAPABILITIES } from 'constants/capabilities'
import { QUERIES } from 'constants/queries'
import { ROUTES } from 'constants/routes'
import { useHasCapability } from 'hooks/useCapabilities'
import Sentry from 'integrations/Sentry'
import { InlineEditField } from 'shared/components/InlineEditField/InlineEditField'
import { Switch } from 'shared/components/Switch'
import { formatPhone } from 'utils/formatters'
import { isPresent } from 'utils/validators'

const StyledSimpleTable = styled.table`
  width: unset !important;
  tr {
    td:nth-child(2) {
      text-align: right;
      padding-left: 20px;
    }
  }
  // Last row if there is more than one
  tr:not(:first-of-type):last-child {
    font-weight: bold;
    border-top: 1px solid ${({ theme }) => theme.COLORS.BORDERS};
  }
`

export type FileDetails = {
  label: string
}

const dataExportFiles: Record<string, FileDetails> = {
  'campaigns.csv.gz': {
    label: 'Campaign Performance',
  },
  'outbound_message_type_usage.csv.gz/segment-based-subscription': {
    label: 'Message Segment Usage',
  },
  'campaign_links.csv.gz': {
    label: 'Campaign Link Performance',
  },
  'members.csv.gz': {
    label: 'Member Details',
  },
  'member_state_changes.csv.gz': {
    label: 'Member Subscription Status',
  },
  'custom_member_data.csv.gz': {
    label: 'Custom Member Data',
  },
  'communities.csv.gz': {
    label: 'Communities',
  },
  'member_communities.csv.gz': {
    label: 'Community Membership',
  },
}

export function General({ client }: { client: Client }) {
  const history = useHistory()
  const queryClient = useQueryClient()
  const [exportFile, setExportFile] = useState('campaigns.csv.gz')
  const canUpdateWhiteLabelEnabled = useHasCapability(CAPABILITIES.ADMIN.WHITE_LABEL_ENABLED.WRITE)

  const communicationChannels = useMemo(
    () => getMemberCountChannels(client.communicationChannels),
    [client.communicationChannels],
  )
  const memberCount = useMemberCount({ clientId: client.id, communicationChannels: communicationChannels })

  const whiteLabelEnabled = client.whiteLabelEnabled ?? false
  const { settings: clientSettings } = useClientSettings(client.id)
  const hasCustomDomain = hasConfiguredCustomDomain(clientSettings?.customDomain)

  const { mutate: saveProfileImage } = useMutation(putProfileImage, {
    onSuccess() {
      notification.success({
        message: ' Profile Image saved.',
        placement: 'bottomRight',
        duration: 2,
      })
      queryClient.invalidateQueries([QUERIES.CLIENT, { id: client.id }])
    },
    onError() {
      notification.error({
        message: 'Profile Image failed to save. Please try again.',
        placement: 'bottomRight',
        duration: 2,
      })
    },
  })
  const impersonate = useImpersonate(client.id)

  const { mutate: deleteClientId } = useMutation(deleteClient, {
    onError: () => {
      notification.error({
        message: 'Could not delete client. Please try again.',
        placement: 'bottomRight',
        duration: 2,
      })
    },
    onSuccess: () => {
      notification.success({
        message: 'Client deleted successfully.',
        placement: 'bottomRight',
        duration: 2,
      })
      queryClient.invalidateQueries([QUERIES.CLIENTS])
      history.push(ROUTES.CLIENTS)
    },
  })
  const { mutate: sendVcard } = useMutation(postSendVcard, {
    onSuccess: () => {
      notification.success({
        message: 'Virtual cards sent successfully.',
        placement: 'bottomRight',
        duration: 2,
      })
    },
  })

  const hasDeleteClientCapability = useHasCapability(CAPABILITIES.ADMIN.CLIENTS.DELETE)
  const hasSendVcardCapability = useHasCapability(CAPABILITIES.ADMIN.CLIENT_VCARD.SEND)

  function invalidateClient() {
    queryClient.invalidateQueries([QUERIES.CLIENT, { id: client.id }])
  }

  function invalidateClientLedger() {
    queryClient.invalidateQueries([QUERIES.CLIENT, { id: client.id }])
  }

  function onFileAccepted(file: File) {
    const body = new FormData()
    body.append('profile_image', file)
    saveProfileImage({ id: client.id, body })
  }

  function handleDownload() {
    getDownloadUrl({ fileName: exportFile, clientId: client.id })
      .then((url) => {
        const link = document.createElement('a')
        link.style.display = 'none'
        link.target = '_blank'
        link.href = url
        let event
        try {
          event = new MouseEvent('click')
        } catch (e) {
          event = document.createEvent('MouseEvent')
          event.initMouseEvent('click', true, true, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null)
        }

        link.dispatchEvent(event)
      })
      .catch((err) => {
        let errorText = `Could not download: ${exportFile}`
        if (err.name === 'ApiError' && err.status === 404) {
          errorText = `No data found: ${exportFile}`
        }
        if (err.name === 'ApiError' && err.status === 401) {
          errorText = `Permission denied: ${exportFile}`
        }
        notification.error({
          message: errorText,
          placement: 'bottomRight',
          duration: 2,
        })

        Sentry.captureException(new Error(errorText))
      })
  }

  const validationText = `${client.firstName} ${client.lastName}`.trim()
  return (
    <SettingsPage description="Profile, display and record details for this client." title="General">
      <SettingsCard title="Brand Profile">
        <SettingsRows>
          <SettingsRow description="Community name shown to members" label="First Name">
            <InlineEditField
              defaultValue={client.firstName}
              name="First Name"
              onSuccess={invalidateClient}
              prepare={(value) => ({
                id: client.id,
                body: { first_name: value },
              })}
              updateEntity={putClient}
              validation={(value) => !!value.match(/^.{3,}$/)}
            />
          </SettingsRow>
          <SettingsRow label="Last Name">
            <InlineEditField
              defaultValue={client.lastName}
              name="Last Name"
              onSuccess={invalidateClient}
              prepare={(value) => ({
                id: client.id,
                body: { last_name: value },
              })}
              updateEntity={putClient}
            />
          </SettingsRow>
          <SettingsRow description="Photo and vCard that members save as a contact" label="Contact Card Photo">
            <Avatar
              alt={[client.firstName, client.lastName].join(' ')}
              aria-label="User profile image"
              icon={<UserOutlined />}
              size={64}
              src={client?.profileImageSmall?.url}
            />
            <Dropzone accept={{ 'image/*': [] }} noDrag onFileAccepted={onFileAccepted}>
              <Button icon={<UploadOutlined />}>Upload</Button>
            </Dropzone>
            <ButtonModalPrompt
              buttonText="Send vCard"
              disabled={!hasSendVcardCapability}
              modalText="This will send a vCard to all SMS members."
              modalTitle="Are you sure?"
              onClick={() => sendVcard(client.id)}
              validationText={validationText}
            />
          </SettingsRow>
          <SettingsRow description="Path in the sign-up link" label="URL Slug">
            <InlineEditField
              defaultValue={client.urlSlug}
              name="URL Slug"
              onSuccess={invalidateClient}
              prepare={(value) => ({
                id: client.id,
                body: { url_slug: value },
              })}
              updateEntity={putClient}
              validation={(value) => !!value.match(/^.{3,}$/)}
            />
          </SettingsRow>
          <SettingsRow label="URL Reference">
            <InlineEditField
              defaultValue={client?.referenceUrl}
              name="URL Reference"
              onSuccess={invalidateClientLedger}
              prepare={(value) => ({
                id: client.id,
                body: { reference_url: value },
              })}
              updateEntity={putClient}
              validation={isPresent.validator}
            />
          </SettingsRow>
        </SettingsRows>
      </SettingsCard>

      <SettingsCard title="Display">
        <SettingsRows>
          <SettingsRow label="Swipe up link">
            <Switch
              defaultChecked={client.swipeUpEnabled}
              name="Swipe up link"
              onSuccess={invalidateClient}
              prepare={(value) => ({
                id: client.id,
                body: { swipe_up_enabled: value },
              })}
              updateEntity={putClient}
            />
          </SettingsRow>
          <SettingsRow
            description="Remove the Community name and branding from various member facing screens."
            label="White-labeling"
          >
            <Switch
              defaultChecked={whiteLabelEnabled}
              disabled={!canUpdateWhiteLabelEnabled || hasCustomDomain}
              name="White-labeling"
              onSuccess={invalidateClient}
              prepare={(value) => ({
                id: client.id,
                body: { white_label_enabled: value },
              })}
              tooltip={
                hasCustomDomain
                  ? 'This client has a custom domain configured. Remove the custom domain before disabling white-labeling.'
                  : undefined
              }
              updateEntity={patchClientSettings}
            />
            {hasCustomDomain && (
              <Alert
                description="Remove the custom domain from the Custom Domain tab before disabling white-labeling."
                message="Custom domain configured"
                showIcon
                style={{ maxWidth: 420, textAlign: 'left' }}
                type="warning"
              />
            )}
          </SettingsRow>
        </SettingsRows>
      </SettingsCard>

      <SettingsCard title="Client Record">
        <SettingsRows>
          <SettingsRow label="Client ID">
            <Typography.Text code copyable>
              {client?.id}
            </Typography.Text>
          </SettingsRow>
          <SettingsRow label="Member Count">
            {memberCount.isLoading ? (
              <Skeleton active loading={memberCount.isLoading} paragraph={false} title={{ width: 50 }} />
            ) : (
              <StyledSimpleTable>
                <tbody>
                  {communicationChannels.map((channel: CommunicationChannel) => (
                    <tr key={channel}>
                      <td>{COMMUNICATION_CHANNEL_LABEL[channel]}:</td>
                      <td>{memberCount[channel]}</td>
                    </tr>
                  ))}
                  {communicationChannels?.length > 1 && (
                    <tr>
                      <td>Total:</td>
                      <td>{memberCount.total}</td>
                    </tr>
                  )}
                </tbody>
              </StyledSimpleTable>
            )}
          </SettingsRow>
          <SettingsRow label="Phone">
            <Typography.Text
              copyable={{
                text: formatPhone(client?.masterPhoneNumber, {
                  intl: false,
                }),
              }}
            >
              {formatPhone(client?.masterPhoneNumber)}
            </Typography.Text>
          </SettingsRow>
          <SettingsRow label="Twilio AccountSID">
            <Typography.Text code copyable>
              {client?.twilioAccountSid}
            </Typography.Text>
          </SettingsRow>
          <SettingsRow label="Connectivity Partner">
            <Typography.Text>{SMS_PROVIDER_LABELS[client.smsProvider] || client.smsProvider}</Typography.Text>
          </SettingsRow>
          <SettingsRow label="Data Exports">
            <select onChange={(e) => setExportFile(e.target.value)} value={exportFile}>
              {Object.entries(dataExportFiles).map(([exportOption, fileDetails]) => (
                <option key={exportOption} value={exportOption}>
                  {fileDetails.label}
                </option>
              ))}
            </select>
            <Button onClick={handleDownload} type="primary">
              Download
            </Button>
          </SettingsRow>
        </SettingsRows>
      </SettingsCard>

      <SettingsCard title="Access">
        <SettingsRows>
          <SettingsRow description="Open the dashboard or the mobile app as this client" label="Impersonate">
            <Button onClick={() => impersonate()} type="primary">
              Impersonate
            </Button>
            <Button onClick={() => impersonate({ mobile: true })}>Impersonate in mobile app</Button>
          </SettingsRow>
        </SettingsRows>
      </SettingsCard>

      {hasDeleteClientCapability && (
        <SettingsCard danger title="Danger Zone">
          <SettingsRows>
            <SettingsRow
              description="Permanently removes the client. Requires typing the client name."
              label="Delete Client"
            >
              <ButtonModalPrompt
                buttonText="Delete"
                danger
                modalText={`This action cannot be undone. Are you sure you want to delete the "${validationText}" client?`}
                modalTitle="WARNING"
                onClick={() => deleteClientId(client.id)}
                validationText={validationText}
              />
            </SettingsRow>
          </SettingsRows>
        </SettingsCard>
      )}
    </SettingsPage>
  )
}
